<script setup lang="ts">
import { ref, watch, computed } from "vue";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import InputNumber from "primevue/inputnumber";
import Tag from "primevue/tag";
import SelectEntity from "../form/SelectEntity.vue";
import { useTicketService } from "src/services/ticket-service";
import { formatCurrency } from "src/utils/currency";
import { useNotification } from "src/composables/useNotification";
import type { FetchArticleResponse, AddArticlesToTicketType as ArticleType } from "src/api/models";
import { AddArticlesToTicketType } from "src/api/models";

const props = defineProps<{
  visible: boolean;
  ticketId?: string;
  editMode?: boolean;
  article?: FetchArticleResponse & {
    quantity?: number;
    association_id?: string;
    type?: ArticleType;
    price?: number | null;
  };
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'article-added': [];
  'article-updated': [];
}>();

const { addArticlesToTicket, updateArticleQuantity, updateArticlePrice } = useTicketService();
const { showSuccess, showError } = useNotification();

// Mode: 'regular' (default) or 'offer'
const addMode = ref<'regular' | 'offer'>('regular');

const selectedArticle = ref<FetchArticleResponse | null>(null);
const articleQuantity = ref(1);
const isSubmitting = ref(false);
const customPrice = ref<number | null>(null);

// Check if editing a fixed/offer type article
const isFixedTypeArticle = computed(() => {
  return props.editMode && props.article?.type === AddArticlesToTicketType.fixed;
});

const dialogTitle = computed(() => {
  if (props.editMode) return "Edit Article";
  if (addMode.value === 'offer') return "Add Article to Offer";
  return "Add Article";
});

const submitButtonLabel = computed(() => {
  if (props.editMode) return "Update";
  if (addMode.value === 'offer') return "Add to Offer";
  return "Add Article";
});

watch(
  () => props.article,
  (newArticle) => {
    if (newArticle && props.editMode) {
      selectedArticle.value = newArticle;
      articleQuantity.value = newArticle.quantity || 1;
      customPrice.value = newArticle.price ?? null;
    }
  },
  { immediate: true }
);

function handleModelChange(value: boolean) {
  emit('update:visible', value);

  if (!value && !props.editMode) {
    resetForm();
  }
}

function resetForm() {
  selectedArticle.value = null;
  articleQuantity.value = 1;
  addMode.value = 'regular';
  customPrice.value = null;
}

async function handleSubmit() {
  if (props.editMode) {
    await handleUpdateArticle();
  } else {
    await handleAddArticle();
  }
}

async function handleAddArticle() {
  if (!selectedArticle.value?.id || !props.ticketId) return;

  isSubmitting.value = true;
  try {
    const isOffer = addMode.value === 'offer';

    const articleData = {
      article_id: selectedArticle.value.id,
      quantity: articleQuantity.value,
      type: isOffer ? AddArticlesToTicketType.fixed : AddArticlesToTicketType.variable,
      price: isOffer ? customPrice.value : null,
    };

    await addArticlesToTicket(props.ticketId, [articleData]);

    resetForm();
    emit('update:visible', false);
    emit('article-added');
    showSuccess({ message: "Article added successfully" });
  } catch (error) {
    showError({ message: "Failed to add article" });
  } finally {
    isSubmitting.value = false;
  }
}

async function handleUpdateArticle() {
  if (!props.article?.association_id) return;

  isSubmitting.value = true;
  try {
    // Update quantity
    await updateArticleQuantity(props.article.association_id, articleQuantity.value);

    // Update price if it's a fixed type article and price has changed
    if (isFixedTypeArticle.value && customPrice.value !== null && customPrice.value !== props.article.price) {
      await updateArticlePrice(props.article.association_id, customPrice.value);
    }

    emit('update:visible', false);
    emit('article-updated');
    showSuccess({ message: "Article updated successfully" });
  } catch (error) {
    showError({ message: "Failed to update article" });
  } finally {
    isSubmitting.value = false;
  }
}


</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="handleModelChange"
    modal
    :header="dialogTitle"
    :style="{ width: '500px' }"
    :dismissableMask="true"
  >
    <div class="flex flex-col gap-4 p-3">
      <!-- Mode Selector (Add Mode Only) -->
      <div v-if="!editMode" class="flex gap-2">
        <Button
          :severity="addMode === 'regular' ? 'primary' : 'secondary'"
          :outlined="addMode !== 'regular'"
          label="Regular"
          icon="pi pi-box"
          @click="addMode = 'regular'"
          class="flex-1"
        />
        <Button
          :severity="addMode === 'offer' ? 'primary' : 'secondary'"
          :outlined="addMode !== 'offer'"
          label="For Offer/Quote"
          icon="pi pi-file"
          @click="addMode = 'offer'"
          class="flex-1"
        />
      </div>

      <!-- Article Selection -->
      <div class="flex flex-col gap-2">
        <label class="font-medium text-gray-800 dark:text-gray-100 text-sm">
          {{ editMode ? 'Article' : 'Select Article' }}
        </label>
        <SelectEntity
          v-if="!editMode"
          entity="articles"
          v-model="selectedArticle"
          :entity-label="['name', 'code', 'sale_price']"
          entity-value="id"
          placeholder="Search articles..."
          :return-entity="true"
        />
        <div v-else class="bg-gray-100 dark:bg-gray-800 p-3 rounded flex items-center gap-2">
          <i class="pi pi-box text-gray-500 dark:text-gray-400"></i>
          <div>
            <div class="font-medium">{{ selectedArticle?.name }}</div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Code: {{ selectedArticle?.code }}</div>
          </div>
        </div>
      </div>

      <!-- Quantity Selection -->
      <div class="flex flex-col gap-2">
        <label class="font-medium text-gray-800 dark:text-gray-100 text-sm">Quantity</label>
        <div class="flex items-center gap-3">
          <InputNumber
            v-model="articleQuantity"
            showButtons
            buttonLayout="horizontal"
            :min="1"
            :step="1"
            class="flex-grow"
            :inputStyle="{ width: '100%' }"
          />
          <div v-if="selectedArticle?.sale_price && addMode === 'regular'" class="whitespace-nowrap">
            <Tag severity="success">
              {{ formatCurrency((selectedArticle.sale_price || 0) * articleQuantity) }}
            </Tag>
          </div>
        </div>
      </div>

      <!-- Offer Mode / Fixed Type: Custom Price Section -->
      <div v-if="addMode === 'offer' || isFixedTypeArticle" class="border border-blue-200 dark:border-blue-800 rounded p-4 bg-blue-50 dark:bg-blue-900/20">
        <div class="text-sm text-blue-700 dark:text-blue-300 mb-3">
          {{ editMode ? 'Edit the custom price for this offer item.' : 'Optionally set a custom price for this offer item.' }}
        </div>

        <div class="flex flex-col gap-2">
          <label class="font-medium text-gray-800 dark:text-gray-100 text-sm">Custom Price (per unit)</label>
          <div class="flex items-center gap-3">
            <InputNumber
              v-model="customPrice"
              mode="currency"
              currency="EUR"
              locale="nl-NL"
              :min="0"
              class="flex-grow"
              :inputStyle="{ width: '100%' }"
              placeholder="Leave empty for default price"
            />
            <div v-if="customPrice" class="whitespace-nowrap">
              <Tag severity="info">
                {{ formatCurrency(customPrice * articleQuantity) }}
              </Tag>
            </div>
          </div>
          <small class="text-gray-500 dark:text-gray-400">
            Default price: {{ formatCurrency(selectedArticle?.sale_price) }}
          </small>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Cancel"
          icon="pi pi-times"
          @click="emit('update:visible', false)"
          text
          severity="secondary"
          :disabled="isSubmitting"
        />
        <Button
          :label="submitButtonLabel"
          :icon="addMode === 'offer' ? 'pi pi-file' : 'pi pi-plus'"
          @click="handleSubmit"
          :disabled="!selectedArticle || isSubmitting"
          :loading="isSubmitting"
          severity="success"
        />
      </div>
    </template>
  </Dialog>
</template>
